// src/pages/CobroSanciones.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
    FaGavel, FaChevronLeft, FaPlus, FaCalendarAlt, FaDollarSign, 
    FaUser, FaHome, FaEye, FaPrint, FaTimes, FaSearch, FaFilter, 
    FaCheckCircle, FaClock, FaFilePdf, FaUndo, FaExclamationTriangle 
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, query, onSnapshot, orderBy, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import ModalCobroMultaAdministrativa from '../components/ModalCobroMultaAdministrativa';
import './CobroSanciones.css';

const CobroSanciones = () => {
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId || userData?.fraccionamientoTd;
    
    const [historial, setHistorial] = useState([]);
    const [casasList, setCasasList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showReciboModal, setShowReciboModal] = useState(false);
    const [reciboData, setReciboData] = useState(null);
    const [loadingRecibo, setLoadingRecibo] = useState(false);
    const [receiptConfig, setReceiptConfig] = useState({});

    // Estados de filtros
    const [filtroTexto, setFiltroTexto] = useState('');
    const [filtroCasa, setFiltroCasa] = useState('todas');
    const [filtroEstado, setFiltroEstado] = useState('todos'); // 'todos' | 'pendientes' | 'saldadas'
    const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
    const [filtroFechaHasta, setFiltroFechaHasta] = useState('');

    // Estados de paginación Zen
    const [paginaActual, setPaginaActual] = useState(1);
    const [elementosPorPagina, setElementosPorPagina] = useState(10);

    // Cargar historial
    useEffect(() => {
        if (!fraccionamientoId) {
            setLoading(false);
            return;
        }

        const historialRef = collection(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas');
        const q = query(historialRef, orderBy('fechaCaptura', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const multas = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setHistorial(multas);
            setLoading(false);
        }, (error) => {
            console.error('Error cargando historial:', error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [fraccionamientoId]);

    // Cargar configuración de recibos
    useEffect(() => {
        const loadReceiptConfig = async () => {
            if (!fraccionamientoId) return;
            try {
                const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
                const snap = await getDoc(fraccRef);
                if (snap.exists()) {
                    const data = snap.data();
                    setReceiptConfig(data.configuracionRecibos || {});
                }
            } catch (error) {
                console.error('Error cargando configuración de recibos:', error);
            }
        };
        loadReceiptConfig();
    }, [fraccionamientoId]);

    // Cargar casas del fraccionamiento para identificar domicilios válidos
    useEffect(() => {
        if (!fraccionamientoId) return;
        const loadCasas = async () => {
            try {
                const casasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'casas');
                const snap = await getDocs(casasRef);
                const list = snap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(c => {
                        const rol = (c.rol || c.rolAsignado || '').toLowerCase();
                        return rol !== 'caseta' && !c.esRolCaseta;
                    })
                    .sort((a, b) => {
                        const numA = (a.numero || a.numeroCasa || '').toString();
                        const numB = (b.numero || b.numeroCasa || '').toString();
                        return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: 'base' });
                    });
                setCasasList(list);
            } catch (err) {
                console.error('Error cargando casas:', err);
            }
        };
        loadCasas();
    }, [fraccionamientoId]);

    // Mapa de identificadores de casas activas
    const mapCasasActivas = useMemo(() => {
        const set = new Set();
        casasList.forEach(c => {
            if (c.id) set.add(String(c.id).trim().toUpperCase());
            const num = String(c.numero || c.numeroCasa || '').trim().toUpperCase();
            if (num) set.add(num);
        });
        return set;
    }, [casasList]);

    // Función de sincronización para saldar sanciones que ya cuentan con un pago registrado en ingresos
    const sincronizarSancionesPagadas = useCallback(async (mostrarAlertas = false) => {
        if (!fraccionamientoId) return;
        try {
            const ingresosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos');
            const snapIngresos = await getDocs(ingresosRef);
            const ingresosList = snapIngresos.docs.map(d => ({ id: d.id, ...d.data() }));

            let actualizadas = 0;
            for (const multa of historial) {
                if (multa.pagada === true || multa.estado === 'saldada') continue;

                const cIdNorm = String(multa.casaId || '').trim().toUpperCase();
                const cNumNorm = String(multa.casaNumero || '').trim().toUpperCase();
                const montoMulta = Number(multa.monto || 0);

                // Buscar un ingreso que cubra esta sanción
                const matchIngreso = ingresosList.find(ing => {
                    const ingCasaId = String(ing.casaId || '').trim().toUpperCase();
                    const ingCasaNum = String(ing.casaNumero || '').trim().toUpperCase();
                    const coincideCasa = (cIdNorm && ingCasaId === cIdNorm) || (cNumNorm && ingCasaNum === cNumNorm);
                    if (!coincideCasa) return false;

                    const cIng = String(ing.concepto || '').toLowerCase();
                    const esMulti = ing.esMultiConcepto || Array.isArray(ing.pagos);

                    if (esMulti && Array.isArray(ing.pagos)) {
                        return ing.pagos.some(p => {
                            const cp = String(p.concepto || '').toLowerCase();
                            const esSanc = cp.includes('sancion') || cp.includes('sansion') || cp.includes('multa');
                            const mp = Number(p.montoNeto || p.monto || 0);
                            return esSanc && Math.abs(mp - montoMulta) < 0.01;
                        });
                    }

                    const esConceptoSanc = cIng.includes('sancion') || cIng.includes('sansion') || cIng.includes('multa');
                    const montoIng = Number(ing.montoNeto || ing.monto || 0);
                    return esConceptoSanc && Math.abs(montoIng - montoMulta) < 0.01;
                });

                if (matchIngreso) {
                    // Actualizar documento de la sanción a saldada
                    await updateDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas', multa.id), {
                        pagada: true,
                        estado: 'saldada',
                        fechaPago: matchIngreso.fecha || matchIngreso.timestamp || new Date(),
                        ingresoId: matchIngreso.id,
                        folioPago: matchIngreso.folio || `PAGO-${matchIngreso.id.slice(-6)}`
                    });

                    // Descontar del adeudo de la casa si aún lo tiene
                    if (multa.casaId) {
                        try {
                            const cRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', multa.casaId);
                            const cSnap = await getDoc(cRef);
                            if (cSnap.exists()) {
                                const cData = cSnap.data() || {};
                                const curAdeudo = Number(cData.adeudo || 0);
                                const curMultas = Number(cData.adeudoMultas || cData.sancionesAdmin || 0);
                                const newAdeudo = Math.max(0, Math.round((curAdeudo - montoMulta) * 100) / 100);
                                const newMultas = Math.max(0, Math.round((curMultas - montoMulta) * 100) / 100);
                                await updateDoc(cRef, {
                                    adeudo: newAdeudo,
                                    adeudoMultas: newMultas,
                                    sancionesAdmin: newMultas
                                });
                            }
                        } catch (errCasa) {
                            console.warn('Error actualizando casa en reconciliación:', errCasa);
                        }
                    }
                    actualizadas++;
                }
            }

            if (mostrarAlertas) {
                if (actualizadas > 0) {
                    alert(`✅ Sincronización completa: Se detectaron y saldaron ${actualizadas} sanciones que ya contaban con pago registrado.`);
                } else {
                    alert('ℹ️ No se detectaron sanciones pendientes con pagos huérfanos.');
                }
            }
        } catch (errSync) {
            console.error('Error al sincronizar sanciones con ingresos:', errSync);
            if (mostrarAlertas) alert('Error al sincronizar sanciones: ' + (errSync.message || 'Error desconocido'));
        }
    }, [fraccionamientoId, historial]);

    // Ejecutar sincronización automática cuando se carguen las sanciones
    useEffect(() => {
        if (historial.length > 0) {
            sincronizarSancionesPagadas(false);
        }
    }, [historial.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Marcar sanción como saldada manualmente
    const handleMarcarComoSaldada = async (multa) => {
        const confirmar = window.confirm(`¿Deseas marcar como SALDADA la sanción de la Casa ${multa.casaNumero || ''} por $${Number(multa.monto || 0).toFixed(2)}? Esta acción amortizará el adeudo correspondiente.`);
        if (!confirmar) return;

        try {
            await updateDoc(doc(db, 'fraccionamientos', fraccionamientoId, 'multasAdministrativas', multa.id), {
                pagada: true,
                estado: 'saldada',
                fechaPago: new Date(),
                observacionesCierre: 'Saldada manualmente por administración'
            });

            if (multa.casaId) {
                const cRef = doc(db, 'fraccionamientos', fraccionamientoId, 'casas', multa.casaId);
                const cSnap = await getDoc(cRef);
                if (cSnap.exists()) {
                    const cData = cSnap.data() || {};
                    const curAdeudo = Number(cData.adeudo || 0);
                    const curMultas = Number(cData.adeudoMultas || cData.sancionesAdmin || 0);
                    const monto = Number(multa.monto || 0);
                    const newAdeudo = Math.max(0, Math.round((curAdeudo - monto) * 100) / 100);
                    const newMultas = Math.max(0, Math.round((curMultas - monto) * 100) / 100);
                    await updateDoc(cRef, {
                        adeudo: newAdeudo,
                        adeudoMultas: newMultas,
                        sancionesAdmin: newMultas
                    });
                }
            }
            alert('✅ Sanción marcada como saldada correctamente.');
        } catch (err) {
            console.error('Error marcando como saldada:', err);
            alert('Error al marcar como saldada: ' + (err.message || 'Error desconocido'));
        }
    };

    const handleVerRecibo = async (multa) => {
        setLoadingRecibo(true);
        setShowReciboModal(true);

        try {
            // Buscar el recibo en recibosHistorial
            const recibosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'recibosHistorial');
            const recibosQuery = query(recibosRef);
            const recibosSnap = await getDocs(recibosQuery);
            
            let reciboEncontrado = null;
            recibosSnap.forEach((doc) => {
                const data = doc.data();
                // Buscar por multaId y casaId, o por referencia que contiene el multaId
                const referenciaMatch = data.referencia && data.referencia.includes(`MULTA-${multa.multaId}`);
                const multaIdMatch = data.multaId === multa.multaId;
                const casaIdMatch = data.casaId === multa.casaId;
                const tipoMatch = data.tipo === 'multa_administrativa';
                
                if ((multaIdMatch || referenciaMatch) && casaIdMatch && tipoMatch) {
                    reciboEncontrado = { id: doc.id, ...data };
                }
            });

            if (reciboEncontrado) {
                setReciboData(reciboEncontrado);
            } else {
                // Si no se encuentra el recibo, crear uno temporal con los datos de la multa
                setReciboData({
                    concepto: `Multa Administrativa: ${multa.multaDescripcion}`,
                    monto: multa.monto,
                    montoNeto: multa.monto,
                    fecha: multa.fechaCaptura,
                    fechaFalta: multa.fechaFalta,
                    casaNumero: multa.casaNumero,
                    nombreResidente: multa.nombreResidente,
                    metodoPago: 'Multa Administrativa',
                    referencia: `MULTA-${multa.multaId}`,
                    observaciones: multa.comentarios || '',
                    folio: `MULTA-${multa.id.slice(-6)}`,
                    multaDescripcion: multa.multaDescripcion,
                });
            }
        } catch (error) {
            console.error('Error cargando recibo:', error);
            // Crear recibo temporal con datos de la multa
            setReciboData({
                concepto: `Multa Administrativa: ${multa.multaDescripcion}`,
                monto: multa.monto,
                montoNeto: multa.monto,
                fecha: multa.fechaCaptura,
                fechaFalta: multa.fechaFalta,
                casaNumero: multa.casaNumero,
                nombreResidente: multa.nombreResidente,
                metodoPago: 'Multa Administrativa',
                referencia: `MULTA-${multa.multaId}`,
                observaciones: multa.comentarios || '',
                folio: `MULTA-${multa.id.slice(-6)}`,
                multaDescripcion: multa.multaDescripcion,
            });
        } finally {
            setLoadingRecibo(false);
        }
    };

    const numeroALetras = (num = 0) => {
        const unidades = ['CERO', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
        const decenas = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
        const tens = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
        const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

        if (num === 0) return 'CERO';
        if (num === 100) return 'CIEN';

        const toWords = (n) => {
            let words = '';
            const c = Math.floor(n / 100);
            const d = Math.floor((n % 100) / 10);
            const u = n % 10;
            if (c) words += `${centenas[c]} `;
            if (d === 1) {
                words += `${decenas[u]} `;
            } else if (d === 2) {
                words += u === 0 ? 'VEINTE ' : `VEINTI${unidades[u].toLowerCase()} `;
            } else if (d > 2) {
                words += tens[d];
                if (u) words += ` Y ${unidades[u]}`;
                words += ' ';
            } else if (u) {
                words += `${unidades[u]} `;
            }
            return words.trim();
        };

        const miles = Math.floor(num / 1000);
        const resto = num % 1000;
        let res = '';
        if (miles) {
            if (miles === 1) res += 'MIL';
            else res += `${toWords(miles)} MIL`;
        }
        if (resto) {
            res += res ? ' ' : '';
            res += toWords(resto);
        }
        return res.trim() || 'CERO';
    };

    const handlePrintRecibo = () => {
        if (!reciboData) return;

        const fechaRecibo = reciboData.fecha?.toDate ? reciboData.fecha.toDate() : new Date(reciboData.fecha);
        const fechaFalta = reciboData.fechaFalta?.toDate ? reciboData.fechaFalta.toDate() : new Date(reciboData.fechaFalta);
        const moneda = (receiptConfig.moneda || 'MXN').toUpperCase();
        const formatMoney = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: moneda }).format(n || 0);
        const total = reciboData.montoNeto || reciboData.monto || 0;
        const totalLetras = numeroALetras(Math.floor(total)) + ' pesos ' + (Math.round((total % 1) * 100)) + '/100 M.N.';

        const html = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Recibo de Sanción Administrativa</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { margin: 0; padding: 20px; font-family: 'Inter', Arial, sans-serif; background: #f8fafc; color: #111827; }
        .receipt-preview { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 30px; color: #111827; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08); max-width: 800px; margin: 0 auto; }
        .preview-row { display: flex; justify-content: space-between; gap: 20px; align-items: flex-start; margin-bottom: 20px; }
        .preview-top { display: flex; gap: 12px; align-items: center; flex: 1; }
        .preview-logo { width: 120px; height: 80px; object-fit: contain; border: 1px dashed #cbd5e1; border-radius: 8px; background: #f8fafc; }
        .preview-condo h4 { margin: 0 0 4px 0; font-size: 18px; font-weight: 600; color: #111827; }
        .preview-condo p { margin: 0; color: #6b7280; font-size: 14px; }
        .preview-right { text-align: right; flex-shrink: 0; }
        .preview-title { margin: 0 0 8px 0; color: #1d4ed8; font-size: 22px; font-weight: 700; letter-spacing: 0.5px; }
        .preview-meta { display: flex; flex-direction: column; gap: 4px; color: #6b7280; font-size: 13px; }
        .preview-divider { border: none; border-top: 1px solid #e5e7eb; margin: 20px 0; }
        .preview-block { margin: 20px 0; }
        .preview-block h5 { margin: 0 0 12px; font-size: 15px; font-weight: 600; color: #1d4ed8; }
        .preview-block p { margin: 6px 0; font-size: 14px; color: #374151; line-height: 1.6; }
        .preview-block p strong { color: #111827; font-weight: 600; }
        .preview-table { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; margin-top: 10px; }
        .preview-table-header, .preview-table-row { display: grid; grid-template-columns: 1fr 140px; padding: 12px 16px; }
        .preview-table-header { background: #f1f5f9; font-weight: 700; font-size: 14px; color: #1f2937; }
        .preview-table-row { border-top: 1px solid #e5e7eb; font-size: 14px; color: #374151; }
        .preview-table-row span:last-child { text-align: right; font-weight: 600; }
        .preview-total { display: flex; justify-content: space-between; align-items: center; font-weight: 700; font-size: 18px; margin-top: 24px; padding: 16px 0; border-top: 2px solid #1d4ed8; color: #111827; }
        .preview-total-amount { color: #1d4ed8; font-size: 22px; }
        .preview-leyenda { font-style: italic; font-size: 13px; color: #6b7280; margin: 12px 0 30px; text-transform: uppercase; }
        .preview-footer { display: flex; justify-content: center; align-items: center; margin-top: 40px; padding-top: 30px; border-top: 1px solid #e5e7eb; }
        .preview-firma-stack { display: flex; flex-direction: column; align-items: center; gap: 16px; }
        .preview-sello { width: 140px; height: 80px; object-fit: contain; border: 1px dashed #cbd5e1; border-radius: 6px; background: #f8fafc; }
        .preview-firma { text-align: center; width: 280px; }
        .preview-firma-line { height: 1px; background: #1f2937; margin: 0 auto 8px; width: 240px; }
        .preview-firma small { color: #6b7280; display: block; font-size: 12px; margin-bottom: 4px; }
        .preview-firma span { color: #1f2937; font-weight: 600; font-size: 14px; }
        @media print { body { background: white; padding: 0; } .receipt-preview { box-shadow: none; border: none; } }
    </style>
</head>
<body>
    <div class="receipt-preview">
        <div class="preview-row">
            <div class="preview-top">
                ${receiptConfig.logoUrl ? `<img src="${receiptConfig.logoUrl}" alt="Logo" class="preview-logo" />` : '<div class="preview-logo"></div>'}
                <div class="preview-condo">
                    <h4>${receiptConfig.condominioNombre || 'Fraccionamiento'}</h4>
                    <p>${receiptConfig.condominioDireccion || ''}</p>
                </div>
            </div>
            <div class="preview-right">
                <h2 class="preview-title">RECIBO DE SANCIÓN ADMINISTRATIVA</h2>
                <div class="preview-meta">
                    <span>Folio: ${reciboData.folio || 'N/A'}</span>
                    <span>Fecha: ${fechaRecibo.toLocaleDateString('es-MX')}</span>
                </div>
            </div>
        </div>
        <hr class="preview-divider" />
        <div class="preview-block">
            <h5>Datos del Residente</h5>
            <p><strong>Casa:</strong> ${reciboData.casaNumero || 'N/A'}</p>
            <p><strong>Residente:</strong> ${reciboData.nombreResidente || 'N/A'}</p>
        </div>
        <div class="preview-block">
            <h5>Detalles de la Sanción</h5>
            <p><strong>Fecha de la Falta:</strong> ${fechaFalta.toLocaleDateString('es-MX')}</p>
            <p><strong>Concepto:</strong> ${reciboData.multaDescripcion || reciboData.concepto || 'N/A'}</p>
            ${reciboData.observaciones ? `<p><strong>Comentarios:</strong> ${reciboData.observaciones}</p>` : ''}
        </div>
        <div class="preview-table">
            <div class="preview-table-header">
                <span>Concepto</span>
                <span>Monto</span>
            </div>
            <div class="preview-table-row">
                <span>${reciboData.multaDescripcion || reciboData.concepto || 'Sanción Administrativa'}</span>
                <span>${formatMoney(total)}</span>
            </div>
        </div>
        <div class="preview-total">
            <span>TOTAL</span>
            <span class="preview-total-amount">${formatMoney(total)}</span>
        </div>
        <p class="preview-leyenda">SON: ${totalLetras}</p>
        <div class="preview-footer">
            <div class="preview-firma-stack">
                ${receiptConfig.selloUrl ? `<img src="${receiptConfig.selloUrl}" alt="Sello" class="preview-sello" />` : '<div class="preview-sello"></div>'}
                <div class="preview-firma">
                    <div class="preview-firma-line"></div>
                    <small>Firma y Sello</small>
                    <span>${receiptConfig.administradorNombre || 'Administración'}</span>
                </div>
            </div>
        </div>
    </div>
</body>
</html>
        `;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.write(html);
            printWindow.document.close();
            printWindow.onload = () => {
                setTimeout(() => {
                    printWindow.print();
                }, 300);
            };
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount || 0);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return 'N/A';
        }
    };

    const formatDateTime = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleString('es-MX', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'N/A';
        }
    };

    // Filtrado reactivo de sanciones
    const historialFiltrado = useMemo(() => {
        return historial.filter((multa) => {
            // 1. Filtro de búsqueda por texto
            if (filtroTexto.trim()) {
                const term = filtroTexto.toLowerCase().trim();
                const casa = String(multa.casaNumero || '').toLowerCase();
                const res = String(multa.nombreResidente || '').toLowerCase();
                const desc = String(multa.multaDescripcion || '').toLowerCase();
                const com = String(multa.comentarios || '').toLowerCase();
                const actor = String(multa.actorNombre || '').toLowerCase();
                const folio = String(multa.folioPago || multa.id || '').toLowerCase();
                const match = casa.includes(term) || res.includes(term) || desc.includes(term) || com.includes(term) || actor.includes(term) || folio.includes(term);
                if (!match) return false;
            }

            // 2. Filtro por Casa
            if (filtroCasa !== 'todas') {
                if (filtroCasa === 'sin_asignar') {
                    const idNorm = String(multa.casaId || '').trim().toUpperCase();
                    const numNorm = String(multa.casaNumero || '').trim().toUpperCase();
                    const tieneCasaActiva = (idNorm && mapCasasActivas.has(idNorm)) || (numNorm && mapCasasActivas.has(numNorm));
                    if (tieneCasaActiva) return false;
                } else {
                    const idNorm = String(multa.casaId || '').trim().toUpperCase();
                    const numNorm = String(multa.casaNumero || '').trim().toUpperCase();
                    const target = String(filtroCasa).trim().toUpperCase();
                    if (idNorm !== target && numNorm !== target) return false;
                }
            }

            // 3. Filtro por Estado
            const estaPagada = multa.pagada === true || multa.estado === 'saldada';
            if (filtroEstado === 'pendientes' && estaPagada) return false;
            if (filtroEstado === 'saldadas' && !estaPagada) return false;

            // 4. Filtro por Rango de Fechas
            const fechaObj = multa.fechaFalta?.toDate ? multa.fechaFalta.toDate() : (multa.fechaFalta ? new Date(multa.fechaFalta) : (multa.fechaCaptura?.toDate ? multa.fechaCaptura.toDate() : new Date(multa.fechaCaptura || 0)));
            if (filtroFechaDesde) {
                const desde = new Date(`${filtroFechaDesde}T00:00:00`);
                if (fechaObj < desde) return false;
            }
            if (filtroFechaHasta) {
                const hasta = new Date(`${filtroFechaHasta}T23:59:59`);
                if (fechaObj > hasta) return false;
            }

            return true;
        });
    }, [historial, filtroTexto, filtroCasa, filtroEstado, filtroFechaDesde, filtroFechaHasta, mapCasasActivas]);

    // Métricas contables del conjunto filtrado
    const metricas = useMemo(() => {
        let total = 0;
        let saldado = 0;
        let pendiente = 0;
        historialFiltrado.forEach((m) => {
            const monto = Number(m.monto || 0);
            total += monto;
            if (m.pagada === true || m.estado === 'saldada') {
                saldado += monto;
            } else {
                pendiente += monto;
            }
        });
        return {
            total,
            saldado,
            pendiente,
            cantidad: historialFiltrado.length
        };
    }, [historialFiltrado]);

    // Reseteo de página al cambiar filtros
    useEffect(() => {
        setPaginaActual(1);
    }, [filtroTexto, filtroCasa, filtroEstado, filtroFechaDesde, filtroFechaHasta]);

    // Paginación Zen
    const totalPaginas = Math.max(1, Math.ceil(historialFiltrado.length / elementosPorPagina));
    const indiceInicio = (paginaActual - 1) * elementosPorPagina;
    const historialPaginado = historialFiltrado.slice(indiceInicio, indiceInicio + elementosPorPagina);

    // Limpiar todos los filtros
    const handleLimpiarFiltros = () => {
        setFiltroTexto('');
        setFiltroCasa('todas');
        setFiltroEstado('todos');
        setFiltroFechaDesde('');
        setFiltroFechaHasta('');
    };

    const hayFiltrosActivos = Boolean(
        filtroTexto.trim() || 
        filtroCasa !== 'todas' || 
        filtroEstado !== 'todos' || 
        filtroFechaDesde || 
        filtroFechaHasta
    );

    // Exportación a PDF e Impresión oficial (TOMA LA TOTALIDAD DE REGISTROS FILTRADOS)
    const handleExportarPDF = () => {
        if (!fraccionamientoId || historialFiltrado.length === 0) {
            alert('No hay registros filtrados para generar el reporte en PDF.');
            return;
        }

        const condominioNombre = receiptConfig.condominioNombre || 'Fraccionamiento';
        const condominioDireccion = receiptConfig.condominioDireccion || '';
        const logoUrl = receiptConfig.logoUrl || '';
        const adminNombre = receiptConfig.administradorNombre || userData?.nombre || 'Administración';
        const selloUrl = receiptConfig.selloUrl || '';

        const fechaEmision = new Date().toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        const resumenFiltros = [
            filtroCasa !== 'todas' ? `Casa: ${filtroCasa === 'sin_asignar' ? 'Sin Asignar' : filtroCasa}` : null,
            filtroEstado !== 'todos' ? `Estado: ${filtroEstado === 'saldadas' ? 'Saldadas' : 'Pendientes'}` : null,
            filtroFechaDesde ? `Desde: ${filtroFechaDesde}` : null,
            filtroFechaHasta ? `Hasta: ${filtroFechaHasta}` : null,
            filtroTexto.trim() ? `Búsqueda: "${filtroTexto}"` : null,
        ].filter(Boolean).join(' | ') || 'Todas las sanciones registradas';

        const filasHtml = historialFiltrado.map((m, idx) => {
            const esSaldada = m.pagada === true || m.estado === 'saldada';
            const fechaFaltaStr = formatDate(m.fechaFalta);
            const fechaCapturaStr = formatDateTime(m.fechaCaptura);
            const estadoBadge = esSaldada
                ? '<span style="background:#dcfce7;color:#166534;padding:4px 8px;border-radius:6px;font-weight:700;font-size:12px;">SALDADA</span>'
                : '<span style="background:#fef3c7;color:#92400e;padding:4px 8px;border-radius:6px;font-weight:700;font-size:12px;">PENDIENTE</span>';
            const folioStr = m.folioPago ? `<div style="font-size:11px;color:#64748b;">Folio: ${m.folioPago}</div>` : '';

            return `
                <tr style="border-bottom: 1px solid #e2e8f0; ${idx % 2 === 1 ? 'background-color:#f8fafc;' : ''}">
                    <td style="padding:10px 8px;font-size:12px;color:#64748b;text-align:center;">${idx + 1}</td>
                    <td style="padding:10px 8px;font-size:12px;color:#1e293b;">
                        <strong>${fechaFaltaStr}</strong>
                        <div style="font-size:11px;color:#64748b;">Cap: ${fechaCapturaStr}</div>
                    </td>
                    <td style="padding:10px 8px;font-size:13px;font-weight:700;color:#0f172a;text-align:center;">
                        Casa ${m.casaNumero || 'S/N'}
                    </td>
                    <td style="padding:10px 8px;font-size:12px;color:#334155;">${m.nombreResidente || 'No registrado'}</td>
                    <td style="padding:10px 8px;font-size:12px;color:#0f172a;">
                        <strong>${m.multaDescripcion || 'Sanción Administrativa'}</strong>
                        ${m.comentarios ? `<div style="font-size:11px;color:#64748b;margin-top:2px;"><em>${m.comentarios}</em></div>` : ''}
                    </td>
                    <td style="padding:10px 8px;font-size:13px;font-weight:700;color:#0f172a;text-align:right;">
                        ${formatCurrency(m.monto || 0)}
                    </td>
                    <td style="padding:10px 8px;text-align:center;">
                        ${estadoBadge}
                        ${folioStr}
                    </td>
                    <td style="padding:10px 8px;font-size:11px;color:#64748b;text-align:center;">${m.actorNombre || 'Admin'}</td>
                </tr>
            `;
        }).join('');

        const htmlReporte = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Reporte de Sanciones Administrativas - ${condominioNombre}</title>
    <style>
        * { box-sizing: border-box; margin: 0; padding: 0; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        body { background: #f8fafc; color: #0f172a; padding: 24px; font-size: 13px; }
        .report-page { max-width: 1050px; margin: 0 auto; background: #ffffff; padding: 32px; border-radius: 12px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); }
        .report-header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 2px solid #e2e8f0; margin-bottom: 24px; }
        .header-brand { display: flex; align-items: center; gap: 16px; }
        .header-logo { width: 90px; height: 60px; object-fit: contain; }
        .header-info h2 { font-size: 20px; color: #0f172a; margin-bottom: 4px; font-weight: 800; }
        .header-info p { color: #64748b; font-size: 13px; }
        .header-meta { text-align: right; }
        .header-meta h1 { font-size: 18px; color: #d97706; margin-bottom: 6px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; }
        .header-meta .date { font-size: 12px; color: #64748b; }
        .header-meta .filters { font-size: 11px; color: #0369a1; background: #e0f2fe; padding: 4px 8px; border-radius: 4px; margin-top: 6px; display: inline-block; font-weight: 600; }
        
        .kpi-container { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 24px; }
        .kpi-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 18px; text-align: center; }
        .kpi-card.purple { border-top: 4px solid #8b5cf6; }
        .kpi-card.green { border-top: 4px solid #10b981; }
        .kpi-card.amber { border-top: 4px solid #f59e0b; }
        .kpi-label { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; margin-bottom: 4px; }
        .kpi-value { font-size: 20px; font-weight: 800; }
        .kpi-card.purple .kpi-value { color: #6d28d9; }
        .kpi-card.green .kpi-value { color: #059669; }
        .kpi-card.amber .kpi-value { color: #d97706; }

        table { width: 100%; border-collapse: collapse; margin-bottom: 28px; }
        thead th { background: #0f172a; color: #ffffff; padding: 10px 8px; font-size: 12px; font-weight: 700; text-align: left; }
        thead th.text-center { text-align: center; }
        thead th.text-right { text-align: right; }

        .report-footer { margin-top: 36px; padding-top: 24px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: flex-end; }
        .footer-note { max-width: 60%; font-size: 11px; color: #94a3b8; line-height: 1.5; }
        .footer-signature { text-align: center; width: 220px; }
        .signature-line { height: 1px; background: #0f172a; margin: 40px auto 8px; width: 180px; }
        .signature-name { font-weight: 700; font-size: 13px; color: #0f172a; }
        .signature-role { font-size: 11px; color: #64748b; }

        @media print {
            body { background: white; padding: 0; }
            .report-page { box-shadow: none; padding: 0; max-width: 100%; }
            tr { page-break-inside: avoid; }
            @page { margin: 1.5cm; size: landscape; }
        }
    </style>
</head>
<body>
    <div class="report-page">
        <div class="report-header">
            <div class="header-brand">
                ${logoUrl ? `<img src="${logoUrl}" alt="Logo" class="header-logo" />` : ''}
                <div class="header-info">
                    <h2>${condominioNombre}</h2>
                    <p>${condominioDireccion}</p>
                </div>
            </div>
            <div class="header-meta">
                <h1>Reporte de Sanciones Administrativas</h1>
                <div class="date">Emisión: ${fechaEmision} (CST)</div>
                <div class="filters">Filtros: ${resumenFiltros}</div>
            </div>
        </div>

        <div class="kpi-container">
            <div class="kpi-card purple">
                <div class="kpi-label">Total Sanciones Filtradas</div>
                <div class="kpi-value">${metricas.cantidad} (${formatCurrency(metricas.total)})</div>
            </div>
            <div class="kpi-card green">
                <div class="kpi-label">Total Saldado (Recaudado)</div>
                <div class="kpi-value">${formatCurrency(metricas.saldado)}</div>
            </div>
            <div class="kpi-card amber">
                <div class="kpi-label">Total Pendiente de Cobro</div>
                <div class="kpi-value">${formatCurrency(metricas.pendiente)}</div>
            </div>
        </div>

        <table>
            <thead>
                <tr>
                    <th class="text-center" style="width:40px;">#</th>
                    <th style="width:130px;">Fecha Falta</th>
                    <th class="text-center" style="width:80px;">Casa</th>
                    <th style="width:160px;">Residente</th>
                    <th>Motivo de la Sanción</th>
                    <th class="text-right" style="width:100px;">Monto</th>
                    <th class="text-center" style="width:120px;">Estado</th>
                    <th class="text-center" style="width:100px;">Registró</th>
                </tr>
            </thead>
            <tbody>
                ${filasHtml}
            </tbody>
        </table>

        <div class="report-footer">
            <div class="footer-note">
                <p><strong>Nota legal y contable:</strong> Este documento constituye el reporte oficial del historial de sanciones administrativas registradas en el sistema para el condominio ${condominioNombre}. La amortización de las sanciones saldadas se encuentra respaldada en los folios de ingresos correspondientes.</p>
            </div>
            <div class="footer-signature">
                ${selloUrl ? `<img src="${selloUrl}" alt="Sello" style="max-height:50px;object-fit:contain;margin-bottom:8px;" />` : ''}
                <div class="signature-line"></div>
                <div class="signature-name">${adminNombre}</div>
                <div class="signature-role">Administración y Tesorería</div>
            </div>
        </div>
    </div>
    <script>
        window.onload = function() {
            setTimeout(function() {
                window.print();
            }, 350);
        };
    </script>
</body>
</html>
        `;

        const printWin = window.open('', '_blank');
        if (printWin) {
            printWin.document.write(htmlReporte);
            printWin.document.close();
        }
    };

    return (
        <div className="cobro-sanciones-page">
            <div className="cobro-sanciones-container">
                <header className="cobro-sanciones-header">
                    <div className="cobro-sanciones-header-icon">
                        <FaGavel />
                    </div>
                    <div>
                        <h1>Cobro de Sanciones</h1>
                        <p>Gestión y registro de sanciones administrativas aplicadas a residentes</p>
                    </div>
                    <Link to="/administracion" className="cobro-sanciones-back-link">
                        <FaChevronLeft /> Regresar a administración
                    </Link>
                </header>

                <section className="cobro-sanciones-content">
                    <div className="cobro-sanciones-actions">
                        <button 
                            type="button" 
                            className="btn-nueva-multa"
                            onClick={() => setShowModal(true)}
                        >
                            <FaPlus /> Nueva Sanción Administrativa
                        </button>
                        <button 
                            type="button" 
                            className="btn-exportar-pdf"
                            onClick={handleExportarPDF}
                            disabled={historialFiltrado.length === 0}
                            title="Exporta a formato PDF profesional todas las sanciones filtradas"
                        >
                            <FaFilePdf /> Exportar Reporte PDF
                        </button>
                        <button 
                            type="button" 
                            className="btn-imprimir-todo"
                            onClick={handleExportarPDF}
                            disabled={historialFiltrado.length === 0}
                            title="Imprime todas las sanciones según los filtros de búsqueda activos"
                        >
                            <FaPrint /> Imprimir Todo
                        </button>
                    </div>

                    {/* Resumen Zen de Métricas */}
                    <div className="sanciones-resumen-grid">
                        <div className="sanciones-kpi-card card-morado">
                            <div className="sanciones-kpi-label">Sanciones Filtradas</div>
                            <div className="sanciones-kpi-valor">{metricas.cantidad}</div>
                            <div className="sanciones-kpi-sub">Total: {formatCurrency(metricas.total)}</div>
                        </div>
                        <div className="sanciones-kpi-card card-verde">
                            <div className="sanciones-kpi-label">Total Saldadas (Recaudado)</div>
                            <div className="sanciones-kpi-valor">{formatCurrency(metricas.saldado)}</div>
                            <div className="sanciones-kpi-sub">
                                {historialFiltrado.filter(m => m.pagada === true || m.estado === 'saldada').length} sanciones liquidadas
                            </div>
                        </div>
                        <div className="sanciones-kpi-card card-naranja">
                            <div className="sanciones-kpi-label">Total Pendiente de Cobro</div>
                            <div className="sanciones-kpi-valor">{formatCurrency(metricas.pendiente)}</div>
                            <div className="sanciones-kpi-sub">
                                {historialFiltrado.filter(m => !m.pagada && m.estado !== 'saldada').length} sanciones pendientes
                            </div>
                        </div>
                    </div>

                    {/* Barra de Filtros Zen */}
                    <div className="sanciones-filtros-bar">
                        <div className="filtro-item filtro-busqueda">
                            <FaSearch className="filtro-icono" />
                            <input
                                type="text"
                                placeholder="Buscar por casa, residente, motivo o folio..."
                                value={filtroTexto}
                                onChange={(e) => setFiltroTexto(e.target.value)}
                            />
                            {filtroTexto && (
                                <button className="btn-clear-input" onClick={() => setFiltroTexto('')}>
                                    <FaTimes />
                                </button>
                            )}
                        </div>

                        <div className="filtro-item">
                            <FaHome className="filtro-icono-inline" />
                            <select value={filtroCasa} onChange={(e) => setFiltroCasa(e.target.value)}>
                                <option value="todas">Todas las casas</option>
                                {casasList.map((c) => (
                                    <option key={c.id} value={c.numero || c.numeroCasa || c.id}>
                                        Casa {c.numero || c.numeroCasa} {c.nombre_residente ? `(${c.nombre_residente})` : ''}
                                    </option>
                                ))}
                                <option value="sin_asignar">⚠️ Sin casa activa asignada</option>
                            </select>
                        </div>

                        <div className="filtro-item">
                            <FaFilter className="filtro-icono-inline" />
                            <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                                <option value="todos">Todos los estados</option>
                                <option value="pendientes">Pendientes de Pago</option>
                                <option value="saldadas">Saldadas / Pagadas</option>
                            </select>
                        </div>

                        <div className="filtro-item filtro-fechas">
                            <FaCalendarAlt className="filtro-icono-inline" />
                            <input
                                type="date"
                                value={filtroFechaDesde}
                                onChange={(e) => setFiltroFechaDesde(e.target.value)}
                                title="Fecha desde"
                            />
                            <span className="filtro-fechas-separador">-</span>
                            <input
                                type="date"
                                value={filtroFechaHasta}
                                onChange={(e) => setFiltroFechaHasta(e.target.value)}
                                title="Fecha hasta"
                            />
                        </div>

                        {hayFiltrosActivos && (
                            <button className="btn-limpiar-filtros" onClick={handleLimpiarFiltros} title="Limpiar todos los filtros">
                                <FaUndo /> Limpiar
                            </button>
                        )}
                    </div>

                    <article className="cobro-sanciones-card">
                        <div className="cobro-sanciones-card-header">
                            <h2 className="section-title">
                                <FaGavel /> Historial de Sanciones Administrativas
                            </h2>
                            <span className="historial-count">
                                {historialFiltrado.length} {historialFiltrado.length === 1 ? 'sanción encontrada' : 'sanciones encontradas'}
                                {hayFiltrosActivos && ` (de ${historial.length} totales)`}
                            </span>
                        </div>

                        {loading ? (
                            <div className="loading-state">
                                <p>Cargando historial...</p>
                            </div>
                        ) : historialFiltrado.length === 0 ? (
                            <div className="empty-state">
                                <FaGavel className="empty-icon" />
                                <p>No se encontraron sanciones con los filtros aplicados</p>
                                {hayFiltrosActivos ? (
                                    <button className="btn-limpiar-filtros-empty" onClick={handleLimpiarFiltros}>
                                        <FaUndo /> Restablecer filtros
                                    </button>
                                ) : (
                                    <small>Haz clic en "Nueva Sanción Administrativa" para comenzar</small>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="historial-table-container">
                                    <table className="historial-table">
                                        <thead>
                                            <tr>
                                                <th>Fecha Falta</th>
                                                <th>Fecha Captura</th>
                                                <th>Casa</th>
                                                <th>Residente</th>
                                                <th>Sanción / Motivo</th>
                                                <th>Monto</th>
                                                <th>Estado</th>
                                                <th>Comentarios</th>
                                                <th>Registrado por</th>
                                                <th>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historialPaginado.map((multa) => {
                                                const esSaldada = multa.pagada === true || multa.estado === 'saldada';
                                                const idNorm = String(multa.casaId || '').trim().toUpperCase();
                                                const numNorm = String(multa.casaNumero || '').trim().toUpperCase();
                                                const tieneCasaActiva = (idNorm && mapCasasActivas.has(idNorm)) || (numNorm && mapCasasActivas.has(numNorm));

                                                return (
                                                    <tr key={multa.id} className={esSaldada ? 'fila-saldada' : 'fila-pendiente'}>
                                                        <td>
                                                            <FaCalendarAlt className="table-icon" />
                                                            {formatDate(multa.fechaFalta)}
                                                        </td>
                                                        <td>{formatDateTime(multa.fechaCaptura)}</td>
                                                        <td>
                                                            <FaHome className="table-icon" />
                                                            <strong>Casa {multa.casaNumero || 'N/A'}</strong>
                                                            {!tieneCasaActiva && (
                                                                <span className="badge-no-asignada" title="Esta casa no se encuentra en el catálogo activo de casas">
                                                                    <FaExclamationTriangle /> No activa
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <FaUser className="table-icon" />
                                                            {multa.nombreResidente || 'N/A'}
                                                        </td>
                                                        <td>
                                                            <strong>{multa.multaDescripcion || 'N/A'}</strong>
                                                        </td>
                                                        <td className="monto-cell">
                                                            <FaDollarSign className="table-icon" />
                                                            {formatCurrency(multa.monto || 0)}
                                                        </td>
                                                        <td>
                                                            {esSaldada ? (
                                                                <div className="badge-estado-sancion saldada" title={multa.folioPago ? `Folio: ${multa.folioPago}` : 'Sanción saldada'}>
                                                                    <FaCheckCircle /> Saldada
                                                                    {multa.folioPago && (
                                                                        <small className="folio-pago-label">{multa.folioPago}</small>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="badge-estado-sancion pendiente" title="Pendiente de pago">
                                                                    <FaClock /> Pendiente
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="comentarios-cell">
                                                            {multa.comentarios || '-'}
                                                        </td>
                                                        <td>{multa.actorNombre || 'N/A'}</td>
                                                        <td>
                                                            <div className="acciones-botones-stack">
                                                                <button
                                                                    className="btn-ver-recibo"
                                                                    onClick={() => handleVerRecibo(multa)}
                                                                    title="Ver recibo de la sanción"
                                                                >
                                                                    <FaEye /> Ver
                                                                </button>
                                                                {!esSaldada && (
                                                                    <button
                                                                        className="btn-saldar-fila"
                                                                        onClick={() => handleMarcarComoSaldada(multa)}
                                                                        title="Marcar sanción como saldada y descontar adeudo"
                                                                    >
                                                                        <FaCheckCircle /> Saldar
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Paginación Zen */}
                                <div className="sanciones-paginacion-container">
                                    <div className="paginacion-info">
                                        Mostrando <strong>{indiceInicio + 1}</strong> - <strong>{Math.min(indiceInicio + elementosPorPagina, historialFiltrado.length)}</strong> de <strong>{historialFiltrado.length}</strong> registros
                                    </div>

                                    <div className="paginacion-controles">
                                        <button
                                            type="button"
                                            className="btn-paginacion-zen"
                                            disabled={paginaActual <= 1}
                                            onClick={() => setPaginaActual(p => Math.max(1, p - 1))}
                                        >
                                            &laquo; Anterior
                                        </button>

                                        <div className="paginacion-paginas-indicador">
                                            Página <strong>{paginaActual}</strong> de <strong>{totalPaginas}</strong>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn-paginacion-zen"
                                            disabled={paginaActual >= totalPaginas}
                                            onClick={() => setPaginaActual(p => Math.min(totalPaginas, p + 1))}
                                        >
                                            Siguiente &raquo;
                                        </button>
                                    </div>

                                    <div className="paginacion-selector-tamano">
                                        <span>Por página:</span>
                                        <select
                                            value={elementosPorPagina}
                                            onChange={(e) => {
                                                setElementosPorPagina(Number(e.target.value));
                                                setPaginaActual(1);
                                            }}
                                        >
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                        </select>
                                    </div>
                                </div>
                            </>
                        )}
                    </article>
                </section>
            </div>

            {/* Modal de Cobro de Multas Administrativas */}
            {showModal && (
                <ModalCobroMultaAdministrativa
                    onClose={() => setShowModal(false)}
                    onConfirm={(data) => {
                        console.log('Multa aplicada:', data);
                        setShowModal(false);
                    }}
                />
            )}

            {/* Modal de Recibo */}
            {showReciboModal && (
                <div className="recibo-modal-overlay" onClick={() => setShowReciboModal(false)}>
                    <div className="recibo-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="recibo-modal-header">
                            <h2>Recibo de Sanción Administrativa</h2>
                            <button className="recibo-modal-close" onClick={() => setShowReciboModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="recibo-modal-body">
                            {loadingRecibo ? (
                                <div className="loading-state">
                                    <p>Cargando recibo...</p>
                                </div>
                            ) : reciboData ? (
                                <div className="recibo-preview-container" id="recibo-preview">
                                    <div className="recibo-preview">
                                        <div className="preview-row preview-top">
                                            <div className="preview-top">
                                                {receiptConfig.logoUrl ? (
                                                    <img src={receiptConfig.logoUrl} alt="Logo" className="preview-logo" />
                                                ) : (
                                                    <div className="preview-logo"></div>
                                                )}
                                                <div className="preview-condo">
                                                    <h4>{receiptConfig.condominioNombre || 'Fraccionamiento'}</h4>
                                                    <p>{receiptConfig.condominioDireccion || ''}</p>
                                                </div>
                                            </div>
                                            <div className="preview-right">
                                                <h2 className="preview-title">RECIBO DE SANCIÓN ADMINISTRATIVA</h2>
                                                <div className="preview-meta">
                                                    <span>Folio: {reciboData.folio || 'N/A'}</span>
                                                    <span>Fecha: {reciboData.fecha?.toDate ? reciboData.fecha.toDate().toLocaleDateString('es-MX') : new Date(reciboData.fecha).toLocaleDateString('es-MX')}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <hr className="preview-divider" />
                                        <div className="preview-block">
                                            <h5>Datos del Residente</h5>
                                            <p><strong>Casa:</strong> {reciboData.casaNumero || 'N/A'}</p>
                                            <p><strong>Residente:</strong> {reciboData.nombreResidente || 'N/A'}</p>
                                        </div>
                                        <div className="preview-block">
                                            <h5>Detalles de la Sanción</h5>
                                            <p><strong>Fecha de la Falta:</strong> {reciboData.fechaFalta?.toDate ? reciboData.fechaFalta.toDate().toLocaleDateString('es-MX') : new Date(reciboData.fechaFalta).toLocaleDateString('es-MX')}</p>
                                            <p><strong>Concepto:</strong> {reciboData.multaDescripcion || reciboData.concepto || 'N/A'}</p>
                                            {reciboData.observaciones && (
                                                <p><strong>Comentarios:</strong> {reciboData.observaciones}</p>
                                            )}
                                        </div>
                                        <div className="preview-table">
                                            <div className="preview-table-header">
                                                <span>Concepto</span>
                                                <span>Monto</span>
                                            </div>
                                            <div className="preview-table-row">
                                                <span>{reciboData.multaDescripcion || reciboData.concepto || 'Sanción Administrativa'}</span>
                                                <span>{formatCurrency(reciboData.montoNeto || reciboData.monto || 0)}</span>
                                            </div>
                                        </div>
                                        <div className="preview-total">
                                            <span>TOTAL</span>
                                            <span className="preview-total-amount">{formatCurrency(reciboData.montoNeto || reciboData.monto || 0)}</span>
                                        </div>
                                        <p className="preview-leyenda">SON: {numeroALetras(Math.floor(reciboData.montoNeto || reciboData.monto || 0))} pesos {Math.round(((reciboData.montoNeto || reciboData.monto || 0) % 1) * 100)}/100 M.N.</p>
                                        <div className="preview-footer">
                                            <div className="preview-firma-stack">
                                                {receiptConfig.selloUrl ? (
                                                    <img src={receiptConfig.selloUrl} alt="Sello" className="preview-sello" />
                                                ) : (
                                                    <div className="preview-sello"></div>
                                                )}
                                                <div className="preview-firma">
                                                    <div className="preview-firma-line"></div>
                                                    <small>Firma y Sello</small>
                                                    <span>{receiptConfig.administradorNombre || 'Administración'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <p>No se pudo cargar el recibo</p>
                                </div>
                            )}
                        </div>
                        <div className="recibo-modal-actions">
                            <button 
                                type="button" 
                                className="btn-exportar-recibo"
                                onClick={handlePrintRecibo}
                                disabled={!reciboData || loadingRecibo}
                            >
                                <FaPrint /> Exportar / Imprimir
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CobroSanciones;

